/**
 * Light and dark.
 *
 * DATA DEFINITION
 *   A ThemePreference is "light" | "dark" | "system".
 *   Interpretation: what the person CHOSE. "system" means follow the operating
 *   system, and keep following it when it changes at sunset — which is why we
 *   store the preference rather than the resolved value.
 *
 *   A Theme is "light" | "dark": the preference resolved against the OS right
 *   now. This is what is actually on the <html> element.
 *
 * The first paint is handled by an inline script in index.html, not here, so
 * the application never flashes the wrong colour scheme before React starts.
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react'

export type ThemePreference = 'light' | 'dark' | 'system'
export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'lattice.theme'
const listeners = new Set<() => void>()

/** Tell every subscriber the theme may have changed. */
const emit = (): void => { for (const l of listeners) l() }

/** What the person chose, defaulting to following the system. */
export function readPreference(): ThemePreference {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    return saved === 'light' || saved === 'dark' ? saved : 'system'
  } catch {
    // Private browsing, or storage disabled. Following the system is the kind
    // default, and the application must work regardless.
    return 'system'
  }
}

/** What the operating system currently prefers. */
const systemTheme = (): Theme =>
  matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

/** The preference resolved to an actual theme. resolveTheme : -> Theme */
export function resolveTheme(): Theme {
  const preference = readPreference()
  return preference === 'system' ? systemTheme() : preference
}

/** Put the resolved theme on the document. applyTheme : -> void */
function applyTheme(): void {
  const theme = resolveTheme()
  document.documentElement.classList.toggle('dark', theme === 'dark')
  document.documentElement.style.colorScheme = theme
}

/** Record a choice and apply it. setThemePreference : ThemePreference -> void */
export function setThemePreference(preference: ThemePreference): void {
  try {
    if (preference === 'system') localStorage.removeItem(STORAGE_KEY)
    else localStorage.setItem(STORAGE_KEY, preference)
  } catch { /* the choice simply will not survive a reload */ }
  applyTheme()
  emit()
}

// Follow the system while the preference is "system", including at sunset.
if (typeof matchMedia !== 'undefined') {
  matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (readPreference() === 'system') { applyTheme(); emit() }
  })
}

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

/**
 * The current theme, the stored preference, and a way to change it.
 * useTheme : -> {theme, preference, setPreference, toggle}
 */
export function useTheme(): {
  theme: Theme
  preference: ThemePreference
  setPreference: (p: ThemePreference) => void
  toggle: () => void
} {
  const theme = useSyncExternalStore(subscribe, resolveTheme, () => 'dark' as Theme)
  const preference = useSyncExternalStore(subscribe, readPreference, () => 'system' as ThemePreference)

  useEffect(applyTheme, [])

  // Toggling from "system" picks the opposite of what is currently showing,
  // which is what someone reaching for a toggle actually means.
  const toggle = useCallback(() => {
    setThemePreference(resolveTheme() === 'dark' ? 'light' : 'dark')
  }, [])

  return { theme, preference, setPreference: setThemePreference, toggle }
}
